import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  OnDestroy,
  ElementRef,
  EventEmitter,
  OnInit,
  QueryList,
  ViewChildren,
  HostListener,
  signal,
  inject,
  ChangeDetectorRef,
  viewChildren,
  computed,
  effect,
  untracked,
  linkedSignal,
  viewChild,
} from '@angular/core';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { BasicStoreService } from '../../services/BasicStore/basic-store.service';
import { APIService } from 'src/app/services/APIService/api.service';
import { first, takeUntil, filter } from 'rxjs/operators';
import { SpinnerService } from 'src/app/services/Spinner/spinner.service';
import { Router } from '@angular/router';
import { AuthService, LoginMethod } from 'src/app/services/Auth/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { Row, Table } from 'src/app/models/ui/table';
import { ColumnFilterAction, ColumnFilterComponent } from '../_components/filter/column-filter/column-filter.component';
import { FilterService, FiltersMap } from '../_components/filter/filter.service';
import { ExcelService } from 'src/app/services/Shared/excel.service';
import QrScanner from 'qr-scanner';
import { MrUnitTextPipe } from '../../pipes/mrUnitText.pipe';
import { TablePrettyPrintPipe } from '../../pipes/tablePrettyPrint.pipe';
import { ProgressbarComponent } from '../_components/progressbar/progressbar.component';
import { DetailviewComponent } from '../_components/detailview/detailview.component';
import { ClarityModule, ClrDatagrid, ClrDatagridColumn, ClrDatagridSortOrder } from '@clr/angular';
import { CommonModule } from '@angular/common';
import { Base64Service } from 'src/app/services/base64/base64.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { ViewType, Viewer } from '@xbim/viewer';
import { IsRep } from 'src/app/services/_offline/dexieDB';
import { KundenPrüfungTypen, StammAuswahl } from 'src/app/types/api';
import { ObjTypePipe } from 'src/app/pipes/objtype.pipe';
import { SOTYP, SOTYP_Map } from 'src/app/models/api/SOTYP';
import { WebSocketService } from 'src/app/services/Websocket/websocket.service';
import { ColumnHideManService } from 'src/app/services/Shared/colhidemanager.service';
import { HideableColumnStateService } from 'src/app/services/Shared/hideable-column-state.service';
import { ReplikatProgressComponent } from '../_components/_modals/replikat-progress/replikat-progress.component';
import { FILTER_TABLE_STORE, provideFilterStore } from 'src/app/shared/stores/filter-table.store';
import { GetByCallbackPipe } from 'src/app/pipes/get-by-callback.pipe';
import { getTokenRight } from 'src/app/misc/functions';
import { ReplikatSetupComponent } from '../_components/_modals/replikat-setup/replikat-setup.component';
import { SetupConfig } from 'src/app/services/_offline/rep-setup.service';
import { IconDgCellComponent } from '../_components/_shared/datagrid-cell/icon-dg-cell.component';
import { ConfirmService } from 'src/app/services/confirm.service';
import { AutofocusDirective } from 'src/app/directives/autofocus.directive';
import { AlertService } from 'src/app/services/alert.service';
import { TableColumnConfig, TableColumnConfigService } from 'src/app/services/Shared/table-column-config.service';
import { ColumnManagerPopupComponent } from '../_components/_shared/column-manager-popup/column-manager-popup.component';
import { MangelModel } from '../_components/_overlays/mangelerfassungsdialog/mangel-erfassung.types';
import { PrüfungsTypStore } from 'src/app/services/Shared/prüfung-typ.store';

interface TreeDataNode {
  name: string;
  stammid: string;
  expanded: boolean;
  lowestLevel: boolean;
  typ: string;
  parent: TreeDataNode | null;
  children: TreeDataNode[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-anlagen',
  templateUrl: './anlagen.component.html',
  styleUrls: ['./anlagen.component.scss'],
  imports: [
    ClarityModule,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ColumnFilterComponent,
    DetailviewComponent,
    ProgressbarComponent,
    ReplikatProgressComponent,
    ReplikatSetupComponent,
    IconDgCellComponent,
    ColumnManagerPopupComponent,
    AutofocusDirective,
    TablePrettyPrintPipe,
    MrUnitTextPipe,
    ObjTypePipe,
    MrTranslatePipe,
    BackendLocaleDatePipe,
    GetByCallbackPipe,
  ],
  providers: [provideFilterStore({ storage: 'anlagen' }), WebSocketService, FilterService, HideableColumnStateService],
})
export class AnlagenComponent implements OnDestroy, OnInit {
  private readonly basicStore = inject(BasicStoreService);
  private readonly apiservice = inject(APIService);
  private readonly spinnerservice = inject(SpinnerService);
  private readonly router = inject(Router);
  protected readonly authService = inject(AuthService);
  private readonly overlayService = inject(OverlayService);
  private readonly toastr = inject(ToastService);
  protected readonly pruefungsTyp = inject(PrüfungsTypStore);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly excelService = inject(ExcelService);
  private readonly base64service = inject(Base64Service);
  private readonly colHideService = inject(ColumnHideManService);
  protected localeDate = inject(BackendLocaleDatePipe);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly alertService = inject(AlertService);
  private readonly confirmService = inject(ConfirmService);
  private readonly tableColumnConfigService = inject(TableColumnConfigService);
  private readonly hideableColumnState = inject(HideableColumnStateService);

  private readonly webSocket = inject(WebSocketService);
  protected readonly filterStore = inject(FILTER_TABLE_STORE);

  private readonly columnFilters = viewChildren(ColumnFilterComponent);

  private readonly grid = viewChild(ClrDatagrid);

  @ViewChild('canvas') canvas: ElementRef<HTMLCanvasElement>;
  @ViewChildren(ClrDatagridColumn) columns: QueryList<ClrDatagridColumn>;

  protected readonly isReplikatProgressOpen = signal(false);
  protected readonly isUserReplikatModalOpen = signal(false);
  protected readonly setupConfig = signal<SetupConfig>(undefined);

  protected readonly allowRepErstellen = computed(() => {
    const rights = this.token()?.rights?.program;
    return rights && getTokenRight(rights, 'AllowPREPCR');
  });

  protected readonly isAnlagenLoading = signal(true);
  protected readonly with_EBL_ALV = signal(true);

  protected readonly table = signal<Table>({ rows: [], columns: [] });
  protected readonly filteredRows = this.filterStore.filteredRows;
  optInterface: number = 0;

  private readonly destroyed$ = new Subject<void>();
  private searchList = ['Bezirk', 'Stellwerk', 'OTYPID', ''];
  listTreeNote: TreeDataNode[] = [];
  rights: any;
  lpid: number = 0;
  showIFC: boolean = false;
  colLoaded: boolean = false;
  isRep = IsRep();
  hides: string[] = [];
  disablePage: boolean = false;

  iconColumns: string[] = ['typ', 'sp', 'st', 'si', 'lf', 'rep', 'minfk'];
  cavnasSet: boolean = false;

  protected readonly selectedRow = signal<Row>(undefined);
  protected readonly ostammid = linkedSignal(() => +this.selectedRow()?.OSTAMMID);

  @ViewChild('alvbem') alvbem: ElementRef;

  showFilters = false;
  alvstatus: number = 0;

  svgFile: any;
  base64Canvas: string;

  protected getExcelList() {
    const sortedCol = this.grid().columns.find(c => c.sortOrder != ClrDatagridSortOrder.UNSORTED);
    if (!sortedCol) {
      this.toastr.warning('Es muss mindestens eine Spalte sortiert sein bevor Sie diese Funktion nutzen können');
      return;
    }

    if (sortedCol) {
      const colID = sortedCol.field;
      const colTyp = sortedCol.colType;
      const isDESC = sortedCol.sortOrder == ClrDatagridSortOrder.DESC;

      const sorted = this.filteredRows().sort((a, b) => {
        switch (colTyp) {
          case 'string':
            return a[colID]?.toString().localeCompare(b[colID]?.toString());
          case 'number':
            return +a[colID] - +b[colID];
        }
      });
      if (isDESC) sorted.reverse();
      this.excelService.exportAsExcelFile(sorted, this.mrTranslate.transform('Anlagen'));
    } else {
      this.excelService.exportAsExcelFile(this.filteredRows(), this.mrTranslate.transform('Anlagen'));
    }
  }

  protected getQRCode() {
    this.qrScanModal = true;
    setTimeout(() => {
      const videoelem = document.getElementById('video') as HTMLVideoElement;
      const qrScanner = new QrScanner(
        videoelem,
        result => {
          console.log('decoded qr code:', result);
          if (result.data.length > 6) {
            this.restoreSelection(Number.parseInt(result.data));
            this.qrScanModal = false;
          }
        },
        {}
      );
      qrScanner.start();
    }, 200);
  }

  protected getSammelausdruck() {
    this.overlayService.setOverlay({
      overlay: 'sammeldialog',
      table: this.table(),
      dataRefresh: this.dataRefresh,
      zindex: 5,
    });
  }

  showoverlay: boolean = false;
  showdetailview: boolean = false;
  hidedetailview: boolean = false;
  showprogressbar: boolean = false;

  split50 = false;
  sidenavenlarged = false;
  progressbarenlarged = false;
  progressbars: any;
  componentStore = {};
  useFAT: boolean = false;
  isAuthLoading = true;
  userPermissions: any = {};
  private authSubscription: Subscription;
  private readonly debugMode = true; // Enable debugging for useFAT issues

  indexScrub: number = -1;

  protected page = 1;
  protected pageCount = 10;

  objectFilter = [];
  filter_invert = false;
  hasMap = true;
  protected readonly token = signal(undefined);

  selOPRUEFID: number = 0;
  setOSTAMMID: number = 0;
  alvHeader: string = '';
  isALVModalVisible: any = false;
  alvtyp: number = 0;
  tableUniqueColumnValues: Record<string, Set<any>>;
  sotyp: SOTYP_Map;
  tableIconColumnValues: Map<string, number>;
  private readonly columnConfigStorageKey = 'Anlagen';

  // Anlagen only ever holds one table at a time, but its column set depends on the
  // selected Prüfungstyp/Anlagenklasse. Mirror the Maengel behaviour (which keeps all
  // tabs in memory) by accumulating every column seen so far, so a reorder made for one
  // Prüfungstyp does not drop the stored order/visibility of the other ones.
  private readonly knownColumns = new Map<string, Table['columns'][number]>();

  PRDEL: boolean = false;
  STDEL: boolean = false;
  otypid: number = 0;
  typid: number = 0;
  qrScanModal = false;
  rechte: number = 1;

  protected readonly isCopyStammOpen = signal(false);
  protected readonly copyStammBez = signal('');
  typ: any;

  //region SRE = Sonic Rail Exporer Ultraschall Messgerät von Vossloh
  //
  @ViewChild('fileUploader') fileUploader: ElementRef;
  @ViewChild('fileIFCUploader') fileIFCUploader: ElementRef;
  showSREModal = false;
  sreFile: any;
  sreList: any;
  sreostammid: any;
  formatDBName: string = '';
  // Flag to unify FAT UI limitation logic (mirrors app.component.ts logic)
  protected readonly isFatUiLimited = computed<boolean>(() => {
    const token = this.token();
    try {
      const optUseFAT = localStorage.getItem('optUseFAT') === 'true';
      const useFAT = token?.rights?.UseFAT === true || this.useFAT === true;
      const dbName = token?.rights?.db?.toLowerCase();
      return dbName === 'mrpro_fat' || optUseFAT || useFAT;
    } catch {
      return false;
    }
  });

  showFilerObjectModal: boolean = false;
  protected otyp_id = signal<number>(undefined);
  // end region
  //

  constructor() {
    effect(() => {
      const selected = this.selectedRow();
      untracked(() => {
        if (selected == null) {
          this.showdetailview = false;
          return;
        }
        this.showdetailview ||= true;

        this.rechte = +selected['rechte'];
        this.typid = +selected['typ_id'];
        this.otypid = +selected['OTYPID'];
        this.copyStammBez.set(`${selected['bezeichnung']}_${this.mrTranslate.transform('Kopie')}`);

        const column = this.columns.toArray().find(col => col.field === 'OSTAMMID');
        const isHidden = (column as any)?.isHidden;
        if (!isHidden) {
          localStorage.setItem('ostammid', this.ostammid().toString());
          localStorage.setItem('otypid', this.otypid.toString());
        }
      });
    });

    this.filterStore.registerTable(this.table);
    this.filterStore.registerFilters(this.columnFilters);
    this.optInterface = +this.authService.decrypt(localStorage.getItem('optInterface'));
    if (!getTokenRight(this.authService.token.rights.program, 'AllowPINTEX')) {
      this.optInterface = 0;
    }
  }

  KSPEZV: StammAuswahl[];
  selKSPEZV: StammAuswahl[];
  KRPUV: KundenPrüfungTypen['kpruv'] = [];
  sxkpo: KundenPrüfungTypen['sxkpo'] = [];
  stamm: any;
  stammdaten: { Bezeichnung: string }[];
  filterselected: string[] = [];
  filterfeld: string;
  isTreeView: boolean = false;
  selected: any = undefined;
  root: TreeDataNode = undefined;

  showModalSammelexport: boolean = false;
  dataSammelexportorg: any = [];
  dataSammelexport: Row[] = [];
  dataBez: any = [];
  dataSTW: any = [];
  dataMBez: any = [];
  protected datumvon: string = BackendLocaleDatePipe.now;
  protected datumbis: string = BackendLocaleDatePipe.now;
  protected datummax: string = BackendLocaleDatePipe.now;
  _name: any = '';
  __name: any = '';
  _stw: any = '*.*';
  _bez: any = '*.*';
  _mbez: any = '*.*';
  __stw: any = '*.*';
  __bez: any = '*.*';
  __mbez: any = '*.*';
  _bereich: number = 0;
  anzSammel: number = 0;
  sammelFilter: any = [];
  dosammel: boolean = false;
  info: string = '';
  pcnt: string = '';
  taskRun: boolean = false;
  showResultText: boolean = false;
  ResultText: any;

  protected SammelTagesbericht() {
    let ostamm = [];
    this.dataSammelexport.forEach(row => {
      if (row.OSTAMMID != 0) ostamm.push(row.OSTAMMID);
    });

    this.dosammel = true;
    this.apiservice
      .getTagesbericht(ostamm)
      .pipe(first())
      .subscribe(res => {
        this.dosammel = false;
        if (res) {
          this.showModalSammelexport = false;
          this.ResultText = res;

          setTimeout(() => {
            this.showResultText = true;
          }, 10);
        }
      });
  }

  protected SammelCheckWMess() {
    let ostamm = [];
    this.dataSammelexport.forEach(row => {
      if (row.OSTAMMID != 0) ostamm.push(row.OSTAMMID);
    });

    let send = {
      otypid: this.otypid,
      ostamm: ostamm,
      von: this.datumvon,
      bis: this.datumbis,
    };

    this.dosammel = true;
    this.apiservice
      .getDokCheckMesswertAusfall(send)
      .pipe(first())
      .subscribe(res => {
        this.dosammel = false;
        if (res.success) {
          this.toastr.success(this.mrTranslate.transform(res.info));
        } else {
          this.showModalSammelexport = false;
          this.ResultText = res.info;

          setTimeout(() => {
            this.showResultText = true;
          }, 10);
        }
      });
  }

  protected async SammelSAP() {
    this.webSocket._config.name = 'Sammel SAP Progress';
    const connectionId = await this.webSocket.startCon();
    this.webSocket.registerHandler('taskStarted', () => (this.taskRun = true));
    this.webSocket.registerHandler('taskProgressChanged', ([info, percent]) => {
      this.taskRun = true;
      this.pcnt = percent;
      this.info = info;
    });
    this.webSocket.registerHandler('taskEnded', () => setTimeout(() => (this.taskRun = false), 500));
    try {
      const pruefids = this.dataSammelexport.flatMap(row => [row.OPRUEFID || undefined]).join(',');
      this.dosammel = true;
      const res = await firstValueFrom(this.apiservice.setSAPExport(pruefids, connectionId));
      if (res['success']) {
        this.showModalSammelexport = false;

        if (res['error'].length) {
          let txt =
            'Es wurden ' + this.dataSammelexport.length + ' Inspektionen exportiert.\n\n Anzahl Mängel: ' + res['done'];
          txt += 'Fehler:\n';
          txt += res['error'].join('\n ');

          this.ResultText = txt;
          this.showResultText = true;
        } else {
          let txt =
            'Es wurden ' + this.dataSammelexport.length + ' Inspektionen exportiert.\n\n Anzahl Mängel: ' + res['done'];
          await this.confirmService.warning(this.mrTranslate.transform(txt), false);
        }
      } else {
        this.taskRun = false;
        this.toastr.error(this.mrTranslate.transform(res['error']));
      }
      this.taskRun = false;
      this.dosammel = false;
    } catch (ex) {
      console.log(ex);
    } finally {
      this.taskRun = false;
      this.webSocket.stopCon();
    }
  }

  protected async SammelPDF() {
    this.webSocket._config.name = 'Sammel PDF Progress';
    const connectionId = await this.webSocket.startCon();
    this.webSocket.registerHandler('taskStarted', () => (this.taskRun = true));
    this.webSocket.registerHandler('taskProgressChanged', ([info, percent]) => {
      this.taskRun = true;
      this.pcnt = percent;
      this.info = info;
    });
    this.webSocket.registerHandler('taskEnded', () => setTimeout(() => (this.taskRun = false), 500));
    try {
      const pruefids = this.dataSammelexport.flatMap(row => [row.OPRUEFID || undefined]).join(',');
      this.dosammel = true;
      const blob = await firstValueFrom(this.apiservice.getPDFFile(pruefids, connectionId));
      if (blob) {
        this.dosammel = false;
        this.showModalSammelexport = false;
        this.overlayService.setOverlay({
          overlay: 'viewAuftrag',
          data: blob,
          zIndex: 10,
          isTopOverlay: true,
        });
      }
    } catch (ex) {
      console.log(ex);
    } finally {
      this.webSocket.stopCon();
    }
  }

  protected SammelExit() {
    this.showModalSammelexport = false;
  }

  protected handleChangeDate(t, val) {
    if (t == 0) {
      this.datumvon = this.localeDate.toBackEndFormat(val);
    } else {
      this.datumbis = this.localeDate.toBackEndFormat(val);
    }
    this.getFildsSammelExport('update');
  }

  protected handleChangeBereich(t, val) {
    this._bereich = t;
  }

  protected sammelSelectedbez(index) {
    this._bez = this.dataBez[index];
    this.getFildsSammelExport('update');
  }
  protected sammelSelectedstw(index) {
    this._stw = this.dataSTW[index];
    this.getFildsSammelExport('update');
  }
  protected sammelSelectedmbez(index) {
    this._mbez = this.dataMBez[index];
    this.getFildsSammelExport('update');
  }

  protected sammelSelectedName(val) {
    this._name = val;
    this.getFildsSammelExport('update');
  }

  protected showSammelExport() {
    this.datumvon = BackendLocaleDatePipe.now;
    this.datumbis = BackendLocaleDatePipe.now;
    this.anzSammel = 0;
    let send = {
      status: 'init',
      von: this.datumvon,
      bis: this.datumbis,
      objektart: 0,
      last: false,
      bez: '*.*',
      stw: '*.*',
      mbez: '*.*',
      name: '',
    };

    this.apiservice
      .sendSammel(send)
      .pipe(first())
      .subscribe((val: any) => {
        if (val.data.rows != 0) {
          this.dataSammelexport = val.data.rows;
          this.dataSammelexportorg = val.data.rows;
          this.anzSammel = val.data.rows.length;
        }
        this.dataBez = val.bez;
        this.dataSTW = val.stw;
        this.dataMBez = val.mbez;
      });

    this.showModalSammelexport = true;
  }

  private getFildsSammelExport(status) {
    this.dataSammelexport = [];
    this.anzSammel = 0;
    this.dataSammelexportorg = [];

    let send = {
      status: status,
      von: this.datumvon,
      bis: this.datumbis,
      objektart: this.otypid,
      bez: this._bez,
      stw: this._stw,
      mbez: this._mbez,
      name: this._name,
    };

    this.apiservice
      .sendSammel(send)
      .pipe(first())
      .subscribe((val: any) => {
        if (val.data.rows != 0) {
          this.dataSammelexportorg = val.data.rows;
          this.dataSammelexport = val.data.rows;
          this.anzSammel = val.data.rows.length;
        }
      });
  }

  protected filterObjektSelected() {
    if (this.showModalSammelexport) {
      this.getFildsSammelExport('update');
    }

    this.selKSPEZV = this.KSPEZV.filter(t => t.OTYPID === this.otypid);
  }

  protected async filterStammSelected(SFELDNA: string) {
    this.filterfeld = SFELDNA;

    this.stammdaten = (await firstValueFrom(this.apiservice.getKSPEZVSelected(this.otypid, SFELDNA))).map(bez => ({
      Bezeichnung: bez,
    }));
  }

  protected showObjektFilters() {
    this.showFilerObjectModal = true;
  }

  protected async setObjektFilters() {
    const sendObj = {
      FilterFeld: this.filterfeld,
      FilterSelected: this.filterselected,
    };

    const result = await firstValueFrom(this.apiservice.getAnlagenFilterStammIDs(this.otypid, sendObj));
    const filters: FiltersMap = {
      OSTAMMID: {
        columnId: 'OSTAMMID',
        items: result.map(item => ({ name: item })),
        reason: ColumnFilterAction.Submit,
      },
    };

    this.filterStore.setFilters(filters);
    this.showFilerObjectModal = false;
  }

  private async loadSOTYP() {
    const map = await firstValueFrom(this.apiservice.getSOTYP());

    if (this.rights?.type) {
      Object.entries(this.rights.type).forEach(([key, value]) => {
        // Check if value array exists and has elements before accessing
        if (!Array.isArray(value) || value.length === 0 || !value[0]?.value) {
          delete map[+key];
        }
      });
    }
    this.sotyp = map;

    this.KSPEZV = await firstValueFrom(this.apiservice.getKSPEZVUsed());
  }

  private async loadPrüfungsTypen() {
    const result = await firstValueFrom(this.apiservice.getKRPUV());
    this.KRPUV = result.kpruv ?? [];
    this.sxkpo = result.sxkpo ?? [];
  }

  protected async changeKPRUV(kpruvid: number) {
    this.pruefungsTyp.change(kpruvid);
    //lade Anlagen neu, da man immer von der gesamten Liste ausgehen muss
    this.spinnerservice.enable();
    this.isAnlagenLoading.set(true);

    const data = await firstValueFrom(this.apiservice.getAnlagen(kpruvid));

    this.filterAnlagenByKPRUVID(data);

    this.filterStore.deleteAllFilters();
    this.basicStore.resetComponentStore('anlagen', { anlagen: this.table() });
    this.basicStore.resetComponentStore('maengel', { maengel: null });
    this.basicStore.deleteComponentStoreAsync('kalender');

    setTimeout(() => {
      this.spinnerservice.disable();
      firstValueFrom(this.apiservice.getMaengel(false, kpruvid)).then(maengel =>
        this.basicStore.resetComponentStore('maengel', { maengel })
      );
    });
  }

  private filterAnlagenByKPRUVID(anlagen: Table) {
    const kpruvid = this.pruefungsTyp.KPRUVID();
    if (kpruvid != 1) {
      const sxkpoFilter = new Set(this.sxkpo.filter(x => x.KPRUVID == kpruvid).map(a => a.OTYPID));
      anlagen.rows = anlagen.rows.filter(x => sxkpoFilter.has(+x.OTYPID));
    }
    this.table.set({ ...anlagen });
    this.applyStoredColumnConfig();
    this.isAnlagenLoading.set(false);
  }

  async ngOnInit() {
    this.alertService.startPolling();
    this.initializeUseFAT();

    let token = this.authService.getToken() ?? { rights: {}, groups: [] };
    token.rights ??= {};
    token.groups ??= [];
    this.token.set(token);

    // Wait for complete authentication and permissions loading
    this.waitForAuthCompletion();
    this.rights = JSON.parse(this.authService.decrypt(localStorage.getItem('rights')));

    this.hasMap = this.authService.canAccessGISMap();
    this.spinnerservice.enable();
    await this.loadPrüfungsTypen();
    await this.getAnlagenStore();
    this.loadSOTYP();
    this.getOptions();

    // Ensure formatDBName is properly set
    this.updateFormatDBName();

    // Subscribe to token changes to update formatDBName when available
    this.authService
      .get$token()
      .pipe(
        takeUntil(this.destroyed$),
        filter(token => token?.db)
      )
      .subscribe(token => {
        token ??= { rights: {}, groups: [] };
        token.rights ??= {};
        token.groups ??= [];
        this.token.set(token);
        this.updateFormatDBName();

        // Update useFAT when token changes
        // this.updateUseFATFromToken();
      });

    if (localStorage.getItem('anlagenPagination'))
      this.pageCount = Number.parseInt(localStorage.getItem('anlagenPagination'));
    if (![0, 10, 20, 50, 100, 500].includes(this.pageCount)) this.disablePage = true;

    // Reload Anlagen list after a FAT has been saved (internal users, no server query changes)
    window.addEventListener('mrpro:fat-updated', this.onFatUpdated);

    // Ensure closing the FAT overlay returns to the table view without showing details
    try {
      let lastOverlay: any = undefined;
      this.overlayService
        .getOverlay()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(ov => {
          // Detect closing of FAT dialog: transition from createFATPruefung -> null
          const wasFat = lastOverlay?.overlay === 'createFATPruefung';
          const isClosed = ov == null;
          if (wasFat && isClosed) {
            // Do not show details when just closing the FAT edit/perform
            this.showdetailview = false;
            this.hidedetailview = false;
            this.ostammid.set(undefined); // avoid triggering setter
            try {
              if (this.grid()) (this.grid() as any).singleSelected = null;
            } catch {}

            // Clear persisted selection to avoid re-opening details on rehydrate
            try {
              const store = this.basicStore.getComponentStore('anlagen');
              if (store) {
                this.basicStore.setComponentStore('anlagen', {
                  ...store,
                  ostammid: undefined,
                  showdetailview: false,
                  hidedetailview: false,
                });
              }
            } catch {}
          }
          lastOverlay = ov;
        });
    } catch {}
  }

  private async getOptions() {
    const { einstellung: { optChecked_EBL = 0 } = {} } = await firstValueFrom(
      this.basicStore.getComponentStoreAsync('einstellung', true)
    );
    this.with_EBL_ALV.set(!!optChecked_EBL);
  }

  private waitForAuthCompletion() {
    this.authSubscription = this.authService.authState$.subscribe(authState => {
      if (authState.isAuthenticated && authState.permissionsLoaded) {
        this.userPermissions = authState.permissions;
        authState.token.groups ??= [];
        this.token.set(authState.token);

        this.isAuthLoading = false;

        // Initialize useFAT with debugging
        this.initializeUseFAT();

        // Trigger UI update
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  /**
   * Initialize useFAT property with comprehensive debugging
   */
  private initializeUseFAT(): void {
    // Set useFAT based on live permission evaluation OR legacy localStorage flag as fallback
    const previous = this.useFAT;
    this.useFAT = this.canCreateWerksabnahme() || localStorage.getItem('optUseFAT') === '1';
    if (this.debugMode && previous !== this.useFAT) {
      console.log('🔧 [DEBUG] useFAT (initialized) =>', this.useFAT, {
        progVersion: this.token()?.rights?.progVersion,
        groups: this.token()?.groups?.length,
        localStorageFlag: localStorage.getItem('optUseFAT'),
      });
    }
  }

  /**
   * Update useFAT from token/permissions reactively
   */
  private updateUseFATFromToken(): void {
    const previousUseFAT = this.useFAT;
    this.useFAT = this.canCreateWerksabnahme() || localStorage.getItem('optUseFAT') === '1';
    if (this.debugMode && previousUseFAT !== this.useFAT) {
      console.log('🔁 [DEBUG] useFAT (updated) =>', this.useFAT);
    }
    if (previousUseFAT !== this.useFAT) {
      this.changeDetectorRef.markForCheck?.();
      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Returns true if user has admin group (DesignOffice Admin or Location Admin)
   * Only admins can delete FAT inspection objects per FATWorkflow.md permission model
   */
  protected readonly isAdminOkta = computed<boolean>(() => {
    const groups = this.token()?.groups;
    if (!Array.isArray(groups) || groups.length === 0) return false;
    return groups.some((g: any) => g?.GroupType === 'admin');
  });

  // New helper: internal or admin (not purely external) user
  protected readonly isInternalOrAdminOkta = computed<boolean>(() => {
    // Use auth service method - returns true when NOT external only
    return !this.authService.isExternalOnlyOktaUser();
  });

  /**
   * Returns true when the user has only external/customer groups
   * i.e., has at least one 'external' group and no 'internal' or 'admin' groups.
   * Also checks group name patterns as fallback when GroupType is not set.
   */
  protected readonly isExternalOnly = computed<boolean>(() => {
    // Use auth service method for consistency
    return this.authService.isExternalOnlyOktaUser();
  });

  /**
   * Returns true if the user is logged in via Okta
   * Uses loginMethod$ signal for reactivity
   */
  protected readonly isOktaUser = computed<boolean>(() => {
    // First try the reactive signal, fall back to getter
    const signalValue = this.authService.loginMethod$?.();
    if (signalValue !== undefined) {
      return signalValue === LoginMethod.Okta;
    }
    // Fallback to non-reactive getter
    return this.authService.loginMethod === LoginMethod.Okta;
  });

  protected canPerformWerksabnahme = (row: any): boolean => {
    if (!row) return false;
    return !this.authService.isExternalOnlyOktaUser();
  };

  protected canEditWerksabnahme = (row: any): boolean => {
    if (!row) return false;
    // External-only User dürfen nicht bearbeiten (nur Ansicht) - Phase 2.1
    return !this.authService.isExternalOnlyOktaUser();
  };

  protected canDeleteWerksabnahme = (row: any): boolean => {
    if (!row) return false;
    // Only admins can delete FAT inspection objects
    return this.isAdminOkta();
  };

  /**
   * Check if user can create Werksabnahme with comprehensive validation
   */
  protected readonly canCreateWerksabnahme = computed<boolean>(() => {
    if (!this.isOktaUser()) return false; // Only Okta users allowed

    // Defensive guards: token may not be ready during early lifecycle
    if (!this.token()) return false; // no rights loaded yet

    const { rights, groups } = this.token();

    // Disallow creation for external-only users (needs groups info, if not yet loaded treat as not external-only to avoid hiding permanently)
    if (Array.isArray(groups) && groups.length > 0 && this.authService.isExternalOnlyOktaUser()) return false;

    // Check progVersion for FAT functionality (fallback safe)
    const hasProgVersion45 = rights?.progVersion === '45';
    const localStorageFAT = localStorage.getItem('optUseFAT') === '1';

    const hasGroups = Array.isArray(groups) && groups.length > 0;

    const canCreate = (hasProgVersion45 || localStorageFAT) && hasGroups;

    if (this.debugMode) {
      // Light conditional log only when token just became available but rights still missing
    }
    return canCreate;
  });

  private updateFormatDBName(): void {
    const formatCurDB = localStorage.getItem('FormatCurDB');
    if (formatCurDB) {
      this.formatDBName = formatCurDB.replace('_', ' ');
    } else {
      // If FormatCurDB is not available, try to get it from the token
      const token = this.authService.getToken();
      if (token?.db) {
        const dbName = token.db.substring(6).replace('_test', ' (Testdatenbank)').replace('_', ' ');
        this.formatDBName = dbName
          .replaceAll(/\b\w{1,3}\b/g, str => str.toUpperCase())
          .replaceAll(/(^|\s)[a-z]/g, str => str.toUpperCase());
        localStorage.setItem('FormatCurDB', this.formatDBName);
      }
    }
  }

  public reloadAnlagen(reloadTabsWaitReady: Subject<any> = undefined) {
    this.spinnerservice.enable();
    if (reloadTabsWaitReady) {
      reloadTabsWaitReady.pipe(first((isReady: any) => isReady?.refreshAnlagen)).subscribe(() => {
        this.getAnlagenStore();
        this.basicStore.setDashboardStore();
      });
    } else {
      this.apiservice
        .getAnlagen(this.pruefungsTyp.KPRUVID())
        .pipe()
        .subscribe((data: any) => {
          let store = this.basicStore.getComponentStore('anlagen');
          this.basicStore.setComponentStore('anlagen', {
            ...store,
            anlagen: data,
            ostammid: this.ostammid() ?? store.ostammid,
          });
          setTimeout(() => {
            this.getAnlagenStore();
          }, 10);
          this.basicStore.setDashboardStore();
        });
    }
  }

  private restoreSelection(id: number) {
    setTimeout(() => {
      if (this.grid()) {
        const all = this.filteredRows();
        const ind = all.findIndex(row => row.OSTAMMID == id);
        if (ind > 6) this.grid().virtualScroll.scrollToIndex(ind - 6);
        this.ostammid.set(id);
        setTimeout(() => (this.grid().selected = [all[ind]]));
      } else this.showdetailview = false;
    });
  }

  protected async stopReplicate() {
    if (!getTokenRight(this.rights.program, 'AllowPREPSY')) {
      this.toastr.warning(this.mrTranslate.transform('Für diese Aktion fehlen die Rechte'));
      return;
    }
    const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie das Replikat zurückspielen wollen?');
    const ok = await this.confirmService.info(text);

    if (ok) this.isReplikatProgressOpen.set(true);
  }

  private async getAnlagenStore() {
    const data = await firstValueFrom(this.basicStore.getComponentStoreAsync('anlagen', true));

    this.filterAnlagenByKPRUVID(data['anlagen']);
    if (!this.table()) return;

    this.hides = this.colHideService.initColHide(this.hides, 'Anlagen') ?? [];
    if (this.isRep) {
      this.hides.push('rep');
    }
    this.applyStoredColumnConfig();
    this.showdetailview ||= data['showdetailview'] ?? false;
    this.hidedetailview ||= data['hidedetailview'] ?? false;
    if (data['ostammid'] || this.ostammid()) {
      this.restoreSelection(data['ostammid'] || this.ostammid());
    }
    this.split50 ||= data['split50'] ?? false;
    this.sidenavenlarged ||= data['sidenavenlarged'] ?? false;
    this.progressbarenlarged ||= data['progressbarenlarged'] ?? false;

    if (localStorage.getItem('anlagenViewMode') == 'tree') this.switchViews(true);
    this.spinnerservice.disable();
  }

  protected removeColFilter() {
    this.hides = this.colHideService.removeColHide('Anlagen');
    this.saveColumnConfig();
  }
  private columnHiddenChangeHandle: ReturnType<typeof setTimeout> | null = null;
  protected hideableState(columnId: string) {
    return this.hideableColumnState.stateFor(columnId, this.hides);
  }

  protected columnHiddenChange() {
    // Clarity emits (clrDgHiddenChange) once per column, so bulk actions like
    // "Alle abwählen"/"Alle auswählen" fire this handler for every column at once.
    // Coalesce the burst into a single service call + one saveColumnConfig() to
    // avoid redundant localStorage writes and change-detection cycles.
    if (this.columnHiddenChangeHandle) clearTimeout(this.columnHiddenChangeHandle);
    this.columnHiddenChangeHandle = setTimeout(() => {
      this.columnHiddenChangeHandle = null;
      this.colHideService.columnHiddenChange(this.columns).then((x: string[]) => {
        // When the change originated from the column manager popup,
        // onColumnConfigChange() already applied the identical hidden set.
        // Skip the redundant reassignment + save so we don't retrigger every
        // hideIDColumns pipe a second time for no value change.
        const merged = this.mergeHiddenForActiveTable(x);
        if (this.sameHiddenSet(merged, this.hides)) return;

        this.hides = merged;
        this.saveColumnConfig();
      });
    }, 60);
  }

  private sameHiddenSet(a: readonly string[], b: readonly string[]): boolean {
    if (a.length !== b.length) return false;
    const setB = new Set(b.map(id => id.toLowerCase()));
    return a.every(id => setB.has(id.toLowerCase()));
  }

  protected onColumnConfigChange(config: TableColumnConfig) {
    const normalizedOrder = config.order.map(id => id.toLowerCase());
    const orderIndex = new Map(normalizedOrder.map((id, index) => [id, index]));

    const mergedHidden = this.mergeHiddenForActiveTable(config.hidden);
    if (!this.sameHiddenSet(mergedHidden, this.hides)) {
      this.hides = mergedHidden;
    }

    const currentColumns = this.table()?.columns ?? [];
    const sortedColumns = [...currentColumns].sort((a, b) => {
      const aIndex = orderIndex.get(a.id.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
      const bIndex = orderIndex.get(b.id.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex;
    });

    // Bulk visibility toggles (select all / deselect all) leave the column order
    // unchanged. Only rebuild the columns signal when the order actually changed
    // to avoid a full datagrid reconciliation pass on every hide/show.
    const orderChanged = sortedColumns.some((col, i) => col.id !== currentColumns[i].id);
    if (orderChanged) {
      this.table.update(table => ({
        ...table,
        columns: sortedColumns,
      }));
    }

    // saveColumnConfig() derives the stored order from the accumulated registry, and Map.set
    // keeps the insertion position of ids it already knows. Without this the grid would show
    // the new order while a reload restored the old one.
    this.reorderKnownColumns(orderIndex);

    this.saveColumnConfig();
  }

  private applyStoredColumnConfig() {
    this.rememberColumns(this.table()?.columns);

    const allColumns = this.getAllColumns();
    if (!allColumns.length) return;

    const applied = this.tableColumnConfigService.applyConfig(this.columnConfigStorageKey, allColumns, this.hides);

    // Keep the registry in the saved order so getAllColumns() (and therefore the next
    // saveColumnConfig()) reproduces it for columns that are not part of the active table.
    this.rememberColumns(applied.columns, true);

    // Sort the active table by the shared saved order instead of overwriting its columns
    // with the union: a Prüfungstyp must not inherit columns of another one. Unknown ids
    // keep their original order at the end.
    const orderIndex = new Map(applied.order.map((id, index) => [id, index]));

    this.hides = [...applied.hidden];
    this.table.update(table => ({
      ...table,
      columns: [...(table?.columns ?? [])].sort((a, b) => {
        const aIndex = orderIndex.get(a.id.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
        const bIndex = orderIndex.get(b.id.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      }),
    }));
  }

  private saveColumnConfig() {
    this.tableColumnConfigService.saveConfig(this.columnConfigStorageKey, {
      order: this.getAllColumns().map(column => column.id),
      hidden: this.hides,
    });
  }

  private mergeHiddenForActiveTable(activeTableHidden: readonly string[]): string[] {
    const activeColumnIds = new Set((this.table()?.columns ?? []).map(column => column.id.toLowerCase()));
    const hidden = new Set(this.hides.map(id => id.toLowerCase()).filter(id => !activeColumnIds.has(id)));

    activeTableHidden.forEach(id => hidden.add(id.toLowerCase()));
    return [...hidden];
  }

  private rememberColumns(columns: Table['columns'] | undefined, reset = false) {
    if (reset) this.knownColumns.clear();

    for (const column of columns ?? []) {
      const id = column?.id?.toLowerCase();
      if (!id) continue;

      // Map.set keeps the original insertion position for known ids, so the accumulated
      // order stays stable while later loads may refresh the column definition.
      this.knownColumns.set(id, column);
    }
  }

  private getAllColumns(): Table['columns'] {
    return [...this.knownColumns.values()];
  }

  /**
   * Brings the accumulated column registry into the order the column manager emitted.
   *
   * The popup only lists the columns of the active Prüfungstyp, so ids of the other ones are not
   * part of that order - a stable sort keeps them in their relative position at the end.
   */
  private reorderKnownColumns(orderIndex: ReadonlyMap<string, number>) {
    const sorted = [...this.knownColumns.entries()].sort(
      ([a], [b]) =>
        (orderIndex.get(a) ?? Number.MAX_SAFE_INTEGER) - (orderIndex.get(b) ?? Number.MAX_SAFE_INTEGER)
    );

    this.knownColumns.clear();
    for (const [id, column] of sorted) this.knownColumns.set(id, column);
  }

  // Reload handler for mrpro:fat-updated
  private readonly onFatUpdated = () => {
    try {
      // Preserve current pagination and filters; close detail view and clear selection
      const prevPage = this.page;

      // Explicitly close detail view and clear any selection to return to table view
      this.showdetailview = false;
      this.hidedetailview = false;
      this.ostammid.set(undefined); // avoid triggering setter side-effects
      try {
        if (this.grid()) (this.grid() as any).singleSelected = null;
      } catch {}

      // Clear ostammid in component store so getAnlagenStore() won't restore selection
      try {
        const store = this.basicStore.getComponentStore('anlagen');
        if (store) {
          this.basicStore.setComponentStore('anlagen', {
            ...store,
            ostammid: undefined,
            showdetailview: false,
            hidedetailview: false,
          });
        }
      } catch {}

      // Trigger data reload; filters are preserved by FilterService
      this.reloadAnlagen();

      // Restore pagination after data comes back
      setTimeout(() => {
        try {
          this.page = prevPage;
        } catch {}
      }, 250);
    } catch {}
  };

  refreshSubscription: Subscription;
  dataRefresh = new EventEmitter();

  public async switchViews(toTree?: boolean) {
    this.spinnerservice.enable();
    this.isTreeView = toTree || !this.isTreeView;

    localStorage.setItem('anlagenViewMode', this.isTreeView ? 'tree' : 'list');
    const data = this.table;

    if (this.isTreeView) {
      this.apiservice
        .getKundenBezeichnung()
        .pipe(first())
        .subscribe((res: any) => {
          this.searchList = [
            res.rows.find(x => x.FeldName == 'Bezirk')?.KBezeichnung ?? 'Bezirk',
            res.rows.find(x => x.FeldName == 'Stellwerk')?.KBezeichnung ?? 'Stellwerk',
            'OTYPID',
            '',
          ];
          let bezirke: any[] = [...new Map(data().rows.map(item => [item[this.searchList[0]], item])).values()];
          bezirke = bezirke.sort((a: any, b: any) => {
            let typeA = a[this.searchList[0]],
              typeB = b[this.searchList[0]];
            if (typeA < typeB) return -1;
            if (typeA > typeB) return 1;
            return 0;
          });

          this.listTreeNote = [];
          let route = JSON.parse(sessionStorage.getItem('treeRoute') ?? '{}');

          for (const element of bezirke) {
            let obj = element;
            if (obj[this.searchList[0]] != '' && obj[this.searchList[0]] != null) {
              let name = this.searchList[0] + ' ' + obj[this.searchList[0]];
              let extend: boolean = route && route.length > 0 ? route[0] == name : false;

              this.root = {
                name: this.searchList[0] + ' ' + obj[this.searchList[0]],
                stammid: obj.OSTAMMID,
                expanded: extend,
                typ: 'blank',
                lowestLevel: false,
                parent: null,
                children: [],
              };
              this.FillChildren(this.root, data().rows, 1, obj[this.searchList[0]], route);

              this.listTreeNote.push(this.root);
            }
          }
        });
    }
    this.spinnerservice.disable();
  }

  public FillChildren(parentNode: TreeDataNode, rows: any, level: number, searchFor: string, route: string[]) {
    let lowestLevel: boolean = this.searchList[level] == '';
    let typLevel: boolean = this.searchList[level] == 'OTYPID';
    const filtered = rows.filter(obj => {
      return obj[this.searchList[level - 1]] === searchFor && searchFor;
    });
    let uniques: any[] = [...new Map(filtered.map(item => [item[this.searchList[level]], item])).values()];
    uniques = uniques.sort((a: any, b: any) => {
      let typeA = a[this.searchList[level]],
        typeB = b[this.searchList[level]];
      if (typeA < typeB) return -1;
      if (typeA > typeB) return 1;
      return 0;
    });

    let goFor = lowestLevel ? filtered : uniques;
    for (const element of goFor) {
      let obj = element;
      if (obj[this.searchList[level]] != '' && (obj[this.searchList[level]] != null || lowestLevel)) {
        let name = this.searchList[level] + ' ' + obj[this.searchList[level]];
        if (lowestLevel) name = obj['typ'] + ' ' + obj['bezeichnung'];
        else if (typLevel) name = obj['typ'];

        let extend: boolean = route && route.length >= level ? route[level] == name : false;

        let node: TreeDataNode = {
          name: name,
          stammid: obj.OSTAMMID,
          expanded: extend,
          typ:
            typLevel ?
              obj['typ_icon'] ?
                'objekttyp' + obj['typ_icon']
              : 'unknown-status'
            : 'blank',
          lowestLevel: lowestLevel,
          parent: parentNode,
          children: [],
        };
        parentNode.children.push(node);
        level = level + 1;
        if (!lowestLevel) this.FillChildren(node, filtered, level, obj[this.searchList[level - 1]], route);
        else if (extend) setTimeout(() => this.treeClick(node), 600);
        level = level - 1;
      }
    }
  }

  public treeClick(stamm: TreeDataNode) {
    if (stamm.lowestLevel) {
      let arr = [stamm.name, stamm.parent.name];
      sessionStorage.setItem('treeRoute', JSON.stringify(this.getUpperDef(arr, stamm.parent).reverse()));
      this.selectedRow.set({ OSTAMMID: +stamm.stammid });
    }
  }
  public getUpperDef(arr: string[], node: TreeDataNode): string[] {
    if (node.parent) {
      arr.push(node.parent.name);
      this.getUpperDef(arr, node.parent);
    }
    return arr;
  }

  protected doimportRSE() {
    let z = 0;
    for (const items of this.sreList) {
      let mangel =
        this.mrTranslate.transform('Ultraschallfehler') +
        ' Width: ' +
        items.Width +
        ' [mm]' +
        ', Height: ' +
        items.Height +
        ' [mm]';
      if (items.ClassificationId > 1) {
        mangel =
          items.ClassificationId.toString() +
          ' - ' +
          items.Schienenfehler?.toString() +
          ' Width: ' +
          items.Width +
          ' [mm]' +
          ', Height: ' +
          items.Height +
          ' [mm]';
      }

      let von = 0;
      let bis = 0;
      if (this.typid == 4) {
        let p = Number.parseFloat(items.LeftX) / 1000;
        von = Number.parseFloat(p.toFixed(3));
        bis = von;
      }

      const form: MangelModel = {
        OSTAMMID: this.ostammid(),
        OTYPID: this.otypid,
        typ_Line_Point: this.typid,
        von: this.typid == 4 ? von : 0,
        bis: this.typid == 4 ? bis : 0,
        Gattung: this.typid == 4 ? this.mrTranslate.transform('Messwerte') : '',
        Stamm: this.typid == 4 ? this.mrTranslate.transform('Ultraschall') : '',
        Pruefart: 'SRE ' + this.mrTranslate.transform('Messung'),
        Lage: this.typid == 3 ? von + ' [m]' : '',
        Mangel: mangel,
        FK: items.Klasse == '' ? 4 : items.Klasse,
        Betriebsgefahr: items.Klasse == 1,
        SZUMZID: 0,
        Faktor: 1,
        Instandsetzung: this.mrTranslate.transform('Ultraschallfehler beheben'),
        nIO: true,
        Umfang: null,
        Mengeneinheit: '',
        Abteilung: '',
        Equipment: '',
        LVPos: '',
        ZFeld1: '',
        ZFeld2: '',
        ZFeld3: '',
        ZFeld4: '',
        ZFeld5: '',
        Bemerkung: `Channel no.: ${items.ChannelNumber}, Amplitude max.: ${items.AmplitudeMaximum}, `,
        lfNr: 0,
      };

      this.apiservice.sendNewMangel(form).then((val: any) => {
        if (val?.success) {
          this.sreList.Import = true;
          z++;
        }
      });
    }

    this.toastr.success(
      this.mrTranslate.transform('Erfolgreich gespeichert') + ' -> [' + z + ']' + this.mrTranslate.transform('Stück'),
      this.mrTranslate.transform('Mangel')
    );

    this.showSREModal = false;
  }

  protected async onFileChange(event, row) {
    const files: FileList = event.target.files;
    if (!files.length) return;

    this.selectedRow.set({ OSTAMMID: row.OSTAMMID });
    this.typid = row.typ_id;
    const file = files[0];
    const src = await this.base64service.getUrlStringFromFile(file);
    let base64 = src.split(',').pop();

    let send = {
      status: 'readxmlSREFile',
      filename: file.name,
      base64string: base64,
    };
    this.sreostammid = row.OSTAMMID;
    this.apiservice
      .setPaintItems(send, '')
      .pipe(first())
      .subscribe((val: any) => {
        if (val?.success) {
          this.sreList = val.list;
          this.showSREModal = true;
          this.selectedRow.set({ OSTAMMID: row.OSTAMMID });
          this.typid = row.typ_id;
        }
        this.fileUploader.nativeElement.value = null;
      });
  }

  protected openFATObjectcreation() {
    // Ensure useFAT is up to date
    // this.updateUseFATFromToken();

    if (!this.canCreateWerksabnahme()) {
      if (this.debugMode) {
        console.error('🚫 [DEBUG] Cannot create Werksabnahme dialog - insufficient permissions');
      }
      this.toastr.error('Keine Berechtigung für Werksabnahme-Erstellung');
      return;
    }

    if (this.refreshSubscription) this.refreshSubscription.unsubscribe();
    this.refreshSubscription = this.dataRefresh?.subscribe(() => {
      this.reloadAnlagen();
    });

    this.overlayService.setOverlay({
      overlay: 'createFATObject',
      zIndex: 8,
      dataRefresh: this.dataRefresh,
    });
  }

  protected openSonderinspektion(row: any) {
    if (this.refreshSubscription) this.refreshSubscription.unsubscribe();
    this.refreshSubscription = this.dataRefresh?.subscribe(() => {
      this.reloadAnlagen();
    });

    this.overlayService.setOverlay({
      overlay: 'sonderinspektionslistedialog',
      ostammid: row.OSTAMMID,
      otypid: row.OTYPID,
      zIndex: 8,
      dataRefresh: this.dataRefresh,
    });
  }

  protected openFATPruefungcreation(ostammid: number, lpid?: number) {
    if (this.refreshSubscription) this.refreshSubscription.unsubscribe();
    this.refreshSubscription = this.dataRefresh?.subscribe(() => {
      this.reloadAnlagen();
    });
    this.overlayService.setOverlay({
      overlay: 'createFATPruefung',
      id: ostammid,
      lpid: lpid ?? 0,
      zIndex: 8,
      dataRefresh: this.dataRefresh,
    });
  }

  protected openFATPruefungcreationEdit(ostammid: number, lpid: number) {
    if (this.refreshSubscription) this.refreshSubscription.unsubscribe();
    this.refreshSubscription = this.dataRefresh?.subscribe(() => {
      this.reloadAnlagen();
    });
    this.overlayService.setOverlay({
      overlay: 'createFATObject',
      id: ostammid,
      lpid: lpid,
      zIndex: 8,
      dataRefresh: this.dataRefresh,
    });
  }

  /**
   * Deletes a FAT inspection (Werksabnahme) object
   * Only admins can perform this action
   * This will delete the entire FAT object including all performed inspections
   */
  protected async deleteWerksabnahme(ostammid: number, lpid: number) {
    const confirmText = this.mrTranslate.transform('Werksabnahme wirklich löschen?');
    const ok = await this.confirmService.danger(confirmText);
    if (!ok) return;

    try {
      this.spinnerservice.enable();
      await firstValueFrom(
        this.apiservice.SysManStartAction('fatDelete', {
          sID: ostammid,
          pID: lpid,
        })
      );
      this.toastr.success(
        this.mrTranslate.transform('Werksabnahme erfolgreich gelöscht'),
        this.mrTranslate.transform('Erfolg')
      );
      // Refresh the table to reflect the deletion
      this.reloadAnlagen();
    } catch (error: any) {
      console.error('Error deleting Werksabnahme:', error);
      this.toastr.error(
        this.mrTranslate.transform('Fehler beim Löschen der Werksabnahme'),
        this.mrTranslate.transform('Fehler')
      );
    } finally {
      this.spinnerservice.disable();
    }
  }

  @HostListener('window:beforeunload')
  ngOnDestroy() {
    this.basicStore.setComponentStore('anlagen', {
      anlagen: this.table(),
      ostammid: this.ostammid(),
      split50: this.split50,
      sidenavenlarged: this.sidenavenlarged,
      showdetailview: this.showdetailview,
      hidedetailview: this.hidedetailview,
      progressbarenlarged: this.progressbarenlarged,
    });

    this.colHideService.setColHide(this.columns, 'Anlagen');
    this.saveColumnConfig();

    if (this.columnHiddenChangeHandle) clearTimeout(this.columnHiddenChangeHandle);
    this.destroyed$.next();
    this.refreshSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.basicStore.deleteComponentStoreAsync('ostammid');
    try {
      window.removeEventListener('mrpro:fat-updated', this.onFatUpdated);
    } catch {}
  }

  protected enlargeSidenav(event) {
    if (event.mode == 'full') {
      this.sidenavenlarged = event.val;
    } else if (event.mode == 'half') {
      this.split50 = event.val;
    } else if (event.mode == 'progress') {
      this.showprogressbar = event.val;
      this.progressbars = {};
    } else if (event.mode == 'hidden') {
      this.hidedetailview = event.val;
    } else if (event.mode == 'close') {
      this.selectedRow.set(null);
      this.showprogressbar = false;
      setTimeout(() => {
        this.showdetailview = false;
      });
    }
  }

  protected readonly anlagenLenght = computed(() => {
    return this.filteredRows()
      .reduce((sum, row) => sum + +row['Länge'], 0)
      .toFixed(1);
  });

  protected handleScrub(event) {
    this.indexScrub = event;
  }

  protected showProgressData(data) {
    if (data) {
      data.once = false;
      this.progressbars = data;
      this.showprogressbar = true;
    }
  }

  protected enlargeProgressbar(val?) {
    if (val) {
      this.progressbarenlarged = true;
    } else {
      this.progressbarenlarged = !this.progressbarenlarged;
    }
  }

  protected openObjectInGISMap(ostammid: number) {
    this.router.navigate(['/karte'], {
      queryParams: { ostammid: ostammid },
    });
  }

  protected async copyStamm() {
    const newbez = this.copyStammBez();
    if (newbez == '' || newbez == undefined) {
      this.toastr.warning(this.mrTranslate.transform('Geben Sie eine Bezeichnung ein'));
      return;
    }

    const sendObj = {
      bezeichnung: newbez,
      otypid: +this.selectedRow().OTYPID,
      typLinePoint: +this.selectedRow().typ_id,
    };

    const { success, error } = await firstValueFrom(this.apiservice.copyStammDaten(this.ostammid(), sendObj));

    if (success) {
      this.reloadAnlagen();

      this.isCopyStammOpen.set(false);
      this.toastr.success(this.mrTranslate.transform('Eintrag erfolgreich gespeichert'));
      this.grid().resize();
    } else {
      this.toastr.warning(error, this.mrTranslate.transform('Ein Fehler ist aufgetreten'));
    }
  }

  protected openNeuStamm(typ: SOTYP) {
    this.refreshSubscription?.unsubscribe();
    this.refreshSubscription = this.dataRefresh?.subscribe(() => {
      this.reloadAnlagen();
    });
    this.overlayService.setOverlay({
      overlay: 'openStammdatenDialog',
      id: -1,
      otypid: typ.id,
      type: typ.typ,
      bezeichnung: typ.bezeichnung,
      zIndex: 5,
      dataRefresh: this.dataRefresh,
    });
  }

  protected set_ALV_Checked(opruefid: number, type: number) {
    this.selOPRUEFID = opruefid;
    this.alvHeader = 'Rückmeldung des Anlagenverantwortlichen';

    if (type == 2) this.alvHeader = 'Rückmeldung des Eisenbahnbetriebsleiters';

    this.isALVModalVisible = true;
    this.alvtyp = type;
  }

  protected updateALVStatus() {
    let b = this.alvbem.nativeElement.value;
    let s = this.alvstatus;
    let toSend = {};
    toSend['OPRUEFID'] = this.selOPRUEFID;
    toSend['Status'] = s;
    toSend['Bemerkung'] = b;
    toSend['Type'] = this.alvtyp;

    this.apiservice
      .setAnlagenALVStatus(toSend)
      .pipe(first())
      .subscribe((res: any) => {
        if (res.status && res.status == 'OK') {
          let index = this.table().rows.findIndex(r => r.LPID === this.selOPRUEFID);

          let row = { ...this.table().rows[index] }; // clone

          if (this.alvtyp === 1) {
            row['ALV'] = true;
            row['ALV Name'] = res.name;
            row['ALV Datum'] = BackendLocaleDatePipe.now;
            row['ALV Bemerkung'] = b;

            switch (this.alvstatus) {
              case 3:
                row['ALV Info'] = 'Gut';
                break;
              case 2:
                row['ALV Info'] = 'Mittel';
                break;
              case 1:
                row['ALV Info'] = 'Schlecht';
                break;
              default:
                row['ALV Info'] = 'Undefiniert';
            }
          } else {
            row['EBL'] = true;
            row['EBL Name'] = res.name;
            row['EBL Datum'] = BackendLocaleDatePipe.now;
            row['EBL Bemerkung'] = b;

            switch (this.alvstatus) {
              case 3:
                row['EBL Info'] = 'Gut';
                break;
              case 2:
                row['EBL Info'] = 'Mittel';
                break;
              case 1:
                row['EBL Info'] = 'Schlecht';
                break;
              default:
                row['EBL Info'] = 'Undefiniert';
            }
          }

          this.table().rows[index] = row;
          this.table.update(table => ({ ...table }));
          //this.reloadAnlagen();
          this.basicStore.resetComponentStore('anlagen', {
            anlagen: this.table(),
          });
          this.isALVModalVisible = false;
          this.toastr.success(this.mrTranslate.transform('Eintrag erfolgreich gespeichert'));
          // this.grid().resize();
        } else {
          this.toastr.warning(res.join('<br>'), this.mrTranslate.transform('Ein Fehler ist aufgetreten'));
        }
      });
  }

  protected normalizedId(id: string): string {
    return id.replaceAll(/\s+/g, '-').replaceAll(/[^a-zA-Z0-9-_]/g, '');
  }

  protected async onIFCFileChange(event, row) {
    const files: FileList = event.target.files;
    if (!files.length) return;

    this.selectedRow.set({ OSTAMMID: row.OSTAMMID });
    this.typid = row.typ_id;
    const file = files[0];
    const src = await this.base64service.getUrlStringFromFile(file);
    let c = {};
    let base64 = src.split(',').pop();
    c['base64'] = base64;
    this.showIFC = true;

    this.apiservice
      .convertIFCFiles(c)
      .pipe(first())
      .subscribe((val: any) => {
        if (val) {
          setTimeout(() => {
            let wexViewer = new Viewer(this.canvas.nativeElement);
            wexViewer.on('loaded', () => {
              wexViewer.show(ViewType.DEFAULT);
            });
            let file = new File([val], 'name');
            wexViewer.load(file);
            wexViewer.start();
          }, 50);
        }
        this.fileIFCUploader.nativeElement.value = null;
      });
  }

  public getChildren = node => node.children;
  select = (obj: any) => {
    if (this.selected?.ID === obj.ID) {
      this.selected = undefined;
    } else {
      this.selected = obj;
    }
  };
}
